// GSC Insights Stage 1 — native Search Console query performance.
// Reads GET /v2/search-demand/:client_id. Search/sort/filter are client-side
// over that payload. Date comparison and landing pages render only when the
// payload says they exist. Heuristic opportunity ranks are labelled as a
// review aid, never as a cause of traffic change.
import { useEffect, useMemo, useState } from 'react';
import { searchDemandAPI } from '@/lib/api';
import { filterQueryRows, formatCount, formatCtr, formatPosition, sortQueryRows } from '@/lib/gscInsightsHelpers';
import { useFounderWorkspace, useFounderPath } from '../FounderWorkspace';
import { Chip, EmptyState, Loading, PageHeader, SecondaryButton, Section, TextButton } from '../ui';

const COLUMNS = [
  { key: 'query', label: 'Query' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'ctr', label: 'CTR' },
  { key: 'position', label: 'Avg. position' },
];

function Metric({ value, formatter }) {
  return <span className="tabular-nums">{formatter(value)}</span>;
}

export default function SearchDemandPage() {
  const { workspaceId, displayName, loadingClients } = useFounderWorkspace();
  const fp = useFounderPath();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [minImpressions, setMinImpressions] = useState('');
  const [hasClicks, setHasClicks] = useState('any');
  const [positionMax, setPositionMax] = useState('');
  const [sortKey, setSortKey] = useState('impressions');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    if (!workspaceId) return undefined;
    let cancelled = false;
    setPayload(null);
    setError(null);
    searchDemandAPI.get(workspaceId)
      .then((res) => { if (!cancelled) setPayload(res.data); })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || 'Search Console could not be read right now.');
      });
    return () => { cancelled = true; };
  }, [workspaceId]);

  const rows = useMemo(() => payload?.queries || [], [payload]);
  const visible = useMemo(
    () => sortQueryRows(filterQueryRows(rows, { q, minImpressions, hasClicks, positionMax }), sortKey, sortDir),
    [rows, q, minImpressions, hasClicks, positionMax, sortKey, sortDir],
  );

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir(key === 'query' ? 'asc' : 'desc'); }
  };

  if (loadingClients || (workspaceId && !payload && !error)) return <Loading label="Reading Search Console…" />;
  if (!workspaceId) {
    return (
      <div data-testid="search-demand-page">
        <EmptyState title="No workspace yet" body="Search Console performance appears once a brand workspace is connected." />
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="search-demand-page">
        <EmptyState testId="search-demand-error" title="Search Console could not be read" body={typeof error === 'string' ? error : 'Try again shortly.'}
          action={<SecondaryButton to={fp('/evidence/connections')} testId="search-demand-open-connections">Open Connections</SecondaryButton>} />
      </div>
    );
  }
  if (payload.state !== 'ready') {
    return (
      <div data-testid="search-demand-page">
        <EmptyState testId="search-demand-unavailable" title="Search Console is not ready"
          body={payload.message || 'Connect Google Search Console to see the searches that already bring people to your site.'}
          action={<SecondaryButton to={fp('/evidence/connections')} testId="search-demand-open-connections">Open Connections</SecondaryButton>} />
      </div>
    );
  }

  const window = payload.window || {};
  const freshness = payload.freshness || {};
  const opportunities = payload.opportunities || {};
  const pages = payload.pages || {};
  const brand = displayName || 'this brand';

  return (
    <div data-testid="search-demand-page">
      <PageHeader
        eyebrow="Search Console"
        title="Search Console"
        summary={`Queries Google Search Console reported for ${brand}. These are recorded search metrics from the connected property, not DataForSEO organic rankings (that is Search), not a forecast, and not a claim that changing a ranking would change traffic.`}
        secondary={<SecondaryButton to={fp('/evidence/connections')} testId="search-demand-connections">Connections</SecondaryButton>}
      />

      <Section eyebrow="Reporting window" testId="search-demand-window">
        <p className="text-[14.5px] text-[var(--text-primary)]" data-testid="search-demand-dates">
          {window.start_date} to {window.end_date}
        </p>
        <p className="mt-1.5 text-[13px] text-[var(--text-secondary)] max-w-[62ch]">{freshness.note}</p>
        {payload.truncated ? (
          <p className="mt-2 text-[13px] text-[var(--text-secondary)]" data-testid="search-demand-truncated">
            Showing the first {rows.length} queries Search Console returned. More queries may exist in this window.
          </p>
        ) : null}
        <p className="mt-2 text-[12.5px] text-[var(--text-tertiary)]">
          Last sync {freshness.last_sync_at || 'not recorded'} · last health check {freshness.last_health_check_at || 'not recorded'}
          {freshness.last_sync_status ? ` · ${freshness.last_sync_status}` : ''}
        </p>
        <p className="mt-1 text-[12.5px] text-[var(--text-tertiary)]" data-testid="search-demand-comparison">
          Date comparison: {payload.comparison_available ? 'available' : 'not available'}. {payload.comparison_reason}
        </p>
      </Section>

      <Section eyebrow="Queries" title={`${formatCount(rows.length)} searches in this window`} testId="search-demand-table">
        <div className="flex flex-wrap gap-3 mb-4">
          <input data-testid="search-demand-filter-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search queries"
            className="h-9 px-3 rounded-[var(--radius-button)] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-primary)] w-56" />
          <input data-testid="search-demand-filter-impressions" value={minImpressions} onChange={(e) => setMinImpressions(e.target.value)} placeholder="Min impressions"
            inputMode="numeric" className="h-9 px-3 rounded-[var(--radius-button)] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-primary)] w-36" />
          <select data-testid="search-demand-filter-clicks" value={hasClicks} onChange={(e) => setHasClicks(e.target.value)}
            className="h-9 px-3 rounded-[var(--radius-button)] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-primary)]">
            <option value="any">Any clicks</option>
            <option value="yes">Has clicks</option>
            <option value="none">Zero clicks</option>
          </select>
          <input data-testid="search-demand-filter-position" value={positionMax} onChange={(e) => setPositionMax(e.target.value)} placeholder="Max avg. position"
            inputMode="decimal" className="h-9 px-3 rounded-[var(--radius-button)] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-primary)] w-40" />
        </div>
        {rows.length === 0 ? (
          <p className="text-[14px] text-[var(--text-secondary)]" data-testid="search-demand-empty-window">Search Console returned no queries in this window.</p>
        ) : visible.length === 0 ? (
          <p className="text-[14px] text-[var(--text-secondary)]" data-testid="search-demand-empty-filter">No queries match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-tertiary)]">
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="py-2 pr-4 font-semibold">
                      <button type="button" data-testid={`search-demand-sort-${col.key}`} onClick={() => toggleSort(col.key)} className="hover:text-[var(--text-primary)]">
                        {col.label}{sortKey === col.key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((row, index) => (
                  <tr key={`${row.query}-${index}`} data-testid="search-demand-row" className="border-t border-[var(--border)]">
                    <td className="py-2.5 pr-4 text-[var(--text-primary)]">{row.query}</td>
                    <td className="py-2.5 pr-4"><Metric value={row.clicks} formatter={formatCount} /></td>
                    <td className="py-2.5 pr-4"><Metric value={row.impressions} formatter={formatCount} /></td>
                    <td className="py-2.5 pr-4"><Metric value={row.ctr} formatter={formatCtr} /></td>
                    <td className="py-2.5 pr-4"><Metric value={row.position} formatter={formatPosition} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section eyebrow="Opportunity candidates" title="Queries worth reviewing first" testId="search-demand-opportunities">
        <p className="text-[13.5px] text-[var(--text-secondary)] max-w-[62ch] mb-4">{opportunities.method}</p>
        {(opportunities.candidates || []).length === 0 ? (
          <p className="text-[14px] text-[var(--text-secondary)]">No ranked candidates in this window.</p>
        ) : (
          <ol className="space-y-4">
            {opportunities.candidates.map((item, index) => (
              <li key={`${item.query}-${index}`} data-testid="search-demand-opportunity" className="border-t border-[var(--border)] pt-3 first:border-t-0 first:pt-0">
                <p className="text-[14.5px] font-medium text-[var(--text-primary)]">{index + 1}. {item.query}</p>
                <p className="mt-1 text-[13px] text-[var(--text-secondary)] tabular-nums">
                  {formatCount(item.clicks)} clicks · {formatCount(item.impressions)} impressions · {formatCtr(item.ctr)} CTR · position {formatPosition(item.position)}
                </p>
                <ul className="mt-2 space-y-1">
                  {(item.reasons || []).map((reason) => (
                    <li key={reason} className="text-[12.5px] text-[var(--text-tertiary)]">{reason}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        )}
      </Section>

      <Section eyebrow="Landing pages" testId="search-demand-pages">
        {pages.available ? (
          <>
            <p className="text-[13.5px] text-[var(--text-secondary)] max-w-[62ch] mb-4">{pages.reason}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[13px]">
                <thead>
                  <tr className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-tertiary)]">
                    <th className="py-2 pr-4 font-semibold">Page</th>
                    <th className="py-2 pr-4 font-semibold">Clicks</th>
                    <th className="py-2 pr-4 font-semibold">Impressions</th>
                    <th className="py-2 pr-4 font-semibold">CTR</th>
                    <th className="py-2 pr-4 font-semibold">Avg. position</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.pages.map((row) => (
                    <tr key={row.page} data-testid="search-demand-page-row" className="border-t border-[var(--border)]">
                      <td className="py-2.5 pr-4 break-all">{row.page}</td>
                      <td className="py-2.5 pr-4"><Metric value={row.clicks} formatter={formatCount} /></td>
                      <td className="py-2.5 pr-4"><Metric value={row.impressions} formatter={formatCount} /></td>
                      <td className="py-2.5 pr-4"><Metric value={row.ctr} formatter={formatCtr} /></td>
                      <td className="py-2.5 pr-4">
                        <Metric value={row.position} formatter={formatPosition} />
                        {row.position == null ? <span className="block text-[11px] text-[var(--text-tertiary)]">{row.position_unmeasured_reason}</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="text-[14px] text-[var(--text-secondary)] max-w-[62ch]" data-testid="search-demand-pages-unavailable">
            {pages.reason || 'Landing-page reporting is unavailable until query–page–date snapshots exist.'}
          </p>
        )}
      </Section>

      <p className="pb-8 text-[12.5px] text-[var(--text-tertiary)]">
        <Chip>Google Search Console</Chip>
        <span className="ml-2">These queries are not Watchlist questions, are not added to weekly AI checks, and are not DataForSEO ranking checks.</span>
        {' '}<TextButton to={fp('/business/watchlist')} testId="search-demand-watchlist">Open Watchlist</TextButton>
      </p>
    </div>
  );
}
